import React, { Fragment, useEffect, useContext, useState } from 'react';
import { Box, Grid, List, ListItem, ListItemText, Typography } from '@material-ui/core';
import VehiclePlaceholder from 'assets/img/vehicle_placeholder.png';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleActions } from 'state/modules/vehicles';
import { customCardStyles, listStyles, useStyles } from './VehicleDetailsWidget.styled';
import EditVehicleDetailsModal from './EditVehicleDetailsModal';
import { UserContext } from 'context';
import { useLocation, Link as DinLink } from 'react-router-dom';
import { buildUrl, parseInputNumber } from 'utils';
import Loading from 'components/Loading';
import { getVehicleActions } from 'utils';
import { ChangeVehicleStatusModal } from 'components/Modals';
import { useConfirm } from 'hooks';
import ItemStatus from 'components/ItemStatus';

const getUnits = (data) => {
  return data
    ? `${data?.magnitude !== undefined ? data?.magnitude : ''} ${data?.units || ''}`
    : '-';
};

const getVehicleDescription = (vehicle) =>
  `${vehicle?.vehicle_type?.year || ''} ${vehicle?.vehicle_type?.make || ''} ${
    vehicle?.vehicle_type?.model || ''
  } ${vehicle?.vehicle_type?.trim || ''}`.trim();

const getVehicleData = (vehicle = {}) => {
  const vehicleType = vehicle?.vehicle_type;

  const assetClass = vehicleType?.asset_class;

  const vehicleInfo = [
    { label: 'Name', value: vehicle.name || '-' },
    { label: 'Vehicle ID', value: vehicle?.id || '-' },
    { label: 'License Plate', value: vehicle?.license_plate || '-' },
    { label: 'VIN', value: vehicle.vin || '-' },
    { label: 'Color', value: vehicle?.color || '-' },
    { label: 'Make', value: vehicleType?.make || '-' },
    { label: 'Model', value: vehicleType?.model || '-' },
    { label: 'Model Year', value: vehicleType?.year || '-' },
    { label: i18n.t('vehicle.trim'), value: vehicleType?.trim || '-' },
    { label: 'Fuel Capacity', value: getUnits(vehicleType?.fuel_capacity) },
    { label: 'Price', value: vehicle?.price ? `$${(vehicle.price / 100).toFixed(2)}` : '-' },
    { label: 'Powertrain', value: vehicleType?.powertrain || '-' },
    // with the conditions field we validate whether or not that item should be shown
    {
      label: 'Number Of Seats',
      value: vehicle?.seats || '-',
      condition: assetClass === 'BUS',
    },
    {
      label: 'Motor',
      value: getUnits(vehicleType?.power),
      condition: assetClass === 'SCOOTER',
    },
    {
      label: 'Battery',
      value: vehicleType?.engine_type || '-',
      condition: assetClass === 'SCOOTER',
    },
    { label: 'Details', value: vehicle?.details || '-' },
    {
      label: 'Range',
      value: getUnits(vehicleType?.range),
      condition: assetClass === 'SCOOTER',
    },
  ];

  return vehicleInfo;
};

export const VehicleDetailsWidget = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const [editionModalOpen, setEditionModalOpen] = useState(false);
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);

  const itemsClasses = listStyles();
  const cardClasses = customCardStyles();
  const vehicle = useSelector((state) => state?.vehicles?.vehicle);
  const loading = useSelector((state) => state?.vehicles?.loading);
  const error = useSelector((state) => state?.vehicles?.errors?.vehicles);
  const core = useSelector((state) => state.config?.settings?.core || {});
  const fetchingUpdateImage = useSelector((state) => state.files?.fetchingUpdateImage);
  const fetchedUpdateImageSuccess = useSelector((state) => state.files?.fetchedUpdateImageSuccess);

  const location = useLocation();
  const routeTo = buildUrl(`/vehicles/${id}`);

  const title =
    location?.pathname === routeTo ? (
      <>ID# {id}</>
    ) : (
      <DinLink to={routeTo}>
        <span className={classes.link}>ID# {id}</span>
      </DinLink>
    );
  const vehicleDescription = getVehicleDescription(vehicle);

  useEffect(() => {
    if (id) {
      dispatch(vehicleActions.fetchVehicleById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id && !fetchingUpdateImage && fetchedUpdateImageSuccess) {
      dispatch(vehicleActions.fetchVehicleById(id));
    }
  }, [fetchingUpdateImage, fetchedUpdateImageSuccess]); // eslint-disable-line

  const toggleEditInformation = (e) => {
    e?.preventDefault();
    setEditionModalOpen(!editionModalOpen);
  };

  const [toggleUnlockAndEnable, UnlockAndEnableConfirm] = useConfirm({
    title: i18n.t('actions.UNLOCKANDENABLE'),
    message: i18n.t('vehicle.unlock_and_enable_confirm'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: () => dispatch(vehicleActions.setVehicleAction(id, 'UNLOCKANDENABLE')),
  });

  const handleMenuItemOnClick = (action) => {
    switch (action) {
      case 'UNLOCKANDENABLE':
        toggleUnlockAndEnable();
        break;

      default:
        dispatch(vehicleActions.setVehicleAction(id, action));
        break;
    }
  };

  const handleChangeStatus = () => {
    dispatch(vehicleActions.resetChangeStatus());
    setOpenUpdateStatus((prevState) => !prevState);
  };

  const handleOnSubmit = (newValues) => {
    let data = { ...newValues };

    delete data.asset_class;

    // remove empty fields for non-emtpy fields
    const nonEmptyField = ['price', 'seats', 'vin'];
    Object.keys(data).forEach(
      (key) => data[key] === '' && nonEmptyField.includes(key) && delete data[key]
    );

    if (data.price) {
      data.price = parseInputNumber(data.price);
    }

    dispatch(vehicleActions.updateVehicle(id, data)).then((response) => {
      if (response.ok) {
        setEditionModalOpen(false);
      }
    });
  };

  const configBrokers = core?.vehicle?.brokers || {};
  let actionsItems;
  if (configBrokers && vehicle?.brokers) {
    const actions = getVehicleActions(configBrokers, vehicle?.brokers) || [];
    actionsItems = [
      {
        title: i18n.t('vehicle.update_information'),
        onClick: toggleEditInformation,
        orgAbility: {
          subject: ['msOwnerOrg', 'plainOrg'],
        },
      },
      { title: i18n.t('vehicle.change_status'), onClick: handleChangeStatus, divider: true },
      ...actions?.map((item) => {
        return {
          title: i18n.t(`actions.${item}`),
          onClick: () => handleMenuItemOnClick(item),
        };
      }),
    ];
  } else {
    actionsItems = [];
  }

  return (
    <Card
      menuItems={loading ? [] : actionsItems}
      menuPermissions={{
        action: 'remote_action',
        subject: 'vehicle',
        type: 'can',
      }}
      title={i18n.t('common.vehicle')}
      headerActionType="vertical"
      data-cy="vehicle-details-widget"
      contentRootClasses={cardClasses}
    >
      <>
        {loading || !id ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : error?.type ? (
          <Box className={classes.errorContainer}>
            {i18n.t(`vehicle.error.${error?.type}`, { defaultValue: error?.message })}
          </Box>
        ) : (
          <div className={classes.cardContentPadding}>
            <Grid container direction="row" alignItems="center" spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h3">
                  <Box className={classes.name}>{title}</Box>
                </Typography>
              </Grid>
              {vehicle?.status && (
                <Grid item xs={12}>
                  <ItemStatus
                    dataCy="vehicle_status"
                    variant="labelValue"
                    label={i18n.t('vehicle.status')}
                    i18nKey="status"
                    status={vehicle?.status}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box textAlign="center">
                  <img
                    alt={vehicle?.name || ''}
                    className={classes.vehicleImg}
                    src={vehicle?.default_file?.thumbnail_url || VehiclePlaceholder}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h4">
                  <Box className={classes.name}>{vehicleDescription}</Box>
                </Typography>
              </Grid>
            </Grid>
            <>
              <List className={classes.vehicleList} data-cy="vehicle-data">
                {getVehicleData(vehicle).map((val, index) => (
                  <Fragment key={index}>
                    {(val?.condition || val?.condition === undefined) && (
                      <ListItem className={classes.vehicleListItem}>
                        <ListItemText
                          classes={itemsClasses}
                          primary={val.label}
                          secondary={val.value}
                        />
                      </ListItem>
                    )}
                  </Fragment>
                ))}
              </List>
            </>
          </div>
        )}
      </>

      {openUpdateStatus && (
        <ChangeVehicleStatusModal
          open={true}
          vehicleId={parseInt(id)}
          vehicle={vehicle}
          handleClick={handleChangeStatus}
        />
      )}
      <UnlockAndEnableConfirm />
      <EditVehicleDetailsModal
        open={editionModalOpen}
        canEdit={orgType === 'msOwnerOrg' || orgType === 'plainOrg'}
        vehicle={vehicle}
        onSubmit={handleOnSubmit}
        onCancel={() => {
          setEditionModalOpen(false);
        }}
      />
    </Card>
  );
};
